import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquareText,
  Server,
  Zap,
  ChevronRight,
  GraduationCap,
  Code2,
  Check,
  Sparkles,
} from "lucide-react";
import { useSettingsStore } from "../../store/settings";
import { GradientButton } from "../ui/GradientButton";
import { PulseIndicator } from "../ui/PulseIndicator";
import { cn } from "../../lib/cn";

interface Props {
  onComplete: () => void;
}

interface Step {
  id: string;
  icon: typeof MessageSquareText;
  eyebrow: string;
  title: string;
  description: string;
  action: string;
  custom?: "mode";
}

const STEPS: Step[] = [
  {
    id: "welcome",
    icon: Sparkles,
    eyebrow: "Шаг 1 из 4",
    title: "ChatGPT, который не только говорит — а делает",
    description:
      "Спрашивайте что угодно, как в обычном чате. А когда нужно — я делаю по-настоящему: пишу и запускаю код, собираю сайты, ботов и документы, ищу в интернете, рисую картинки. Не просто советую — выполняю.",
    action: "Начать",
  },
  {
    id: "ready-examples",
    icon: Zap,
    eyebrow: "Шаг 2 из 4",
    title: "Что можно попросить",
    description:
      "Напишите задачу простым языком — остальное я беру на себя. Например: «Сделай телеграм-бота», «Напиши и запусти Python-скрипт», «Собери резюме в PDF», «Найди и сравни цены в таблице».",
    action: "Дальше",
  },
  {
    id: "mode",
    icon: GraduationCap,
    eyebrow: "Шаг 3 из 4",
    title: "Выберите, как показывать ответы",
    description:
      "Простой — общаемся как в чате, я даю готовый результат по-человечески. Разработчик — дополнительно видно план, команды, diff и терминал. Поменять можно в любой момент.",
    action: "Продолжить",
    custom: "mode",
  },
  {
    id: "ready",
    icon: Server,
    eyebrow: "Шаг 4 из 4",
    title: "Готово — пишите первое сообщение",
    description:
      "Просто начните диалог. Хотите запускать проекты и держать их на сервере без ограничений — подключите свой VPS или тариф позже, прямо из чата.",
    action: "Открыть чат",
  },
];

export function OnboardingWizard({ onComplete }: Props) {
  const [step, setStep] = useState(0);
  const setDisplayMode = useSettingsStore((s) => s.setDisplayMode);
  const displayMode = useSettingsStore((s) => s.displayMode);

  function next() {
    if (step < STEPS.length - 1) {
      setStep((s) => s + 1);
    } else {
      localStorage.setItem("aicores.onboarded", "true");
      onComplete();
    }
  }

  function back() {
    if (step > 0) setStep((s) => s - 1);
  }

  const current = STEPS[step];
  const Icon = current.icon;

  return (
    <div className="min-h-screen bg-bg text-fg overflow-hidden relative">
      {/* Mesh + grid */}
      <div className="fixed inset-0 pointer-events-none mesh-bg" aria-hidden />
      <div
        className="fixed inset-0 pointer-events-none bg-grid-fine bg-grid-size opacity-[0.35] dark:opacity-[0.18]"
        aria-hidden
      />
      {/* Floating blobs */}
      <div
        className="pointer-events-none fixed -top-24 -right-12 size-[460px] rounded-full opacity-50 blur-3xl animate-blob"
        style={{ background: "radial-gradient(circle, rgb(var(--accent-500) / 0.30) 0%, transparent 70%)" }}
        aria-hidden
      />
      <div
        className="pointer-events-none fixed -bottom-24 -left-12 size-[460px] rounded-full opacity-50 blur-3xl animate-blob"
        style={{
          background: "radial-gradient(circle, rgb(var(--violet) / 0.30) 0%, transparent 70%)",
          animationDelay: "4s",
        }}
        aria-hidden
      />

      <div className="relative min-h-screen flex flex-col items-center justify-center p-4 py-10">
        {/* Progress dots */}
        <div className="mb-8 flex items-center gap-2">
          {STEPS.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setStep(i)}
              aria-label={`Перейти на шаг ${i + 1}`}
              className={cn(
                "h-1.5 rounded-full transition-all duration-300",
                i === step ? "w-10 bg-grad-cta" : i < step ? "w-5 bg-accent-500/40" : "w-5 bg-border",
              )}
            />
          ))}
        </div>

        <div className="w-full max-w-3xl grid lg:grid-cols-[1fr_1.1fr] gap-8 lg:gap-12 items-center">
          {/* ─── LEFT: text + actions ────────────────────────────────────── */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 8 }}
              transition={{ duration: 0.22 }}
              className="space-y-5 text-center lg:text-left"
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-border/50 bg-bg-card/70 backdrop-blur px-3 py-1 text-[11px] uppercase tracking-wider text-fg-subtle font-mono">
                <Icon className="size-3.5 text-accent-500" />
                {current.eyebrow}
              </div>

              <h1 className="text-display text-balance">
                <span className="grad-text">{current.title}</span>
              </h1>

              <p className="text-fg-muted text-pretty leading-relaxed">{current.description}</p>

              {/* Mode picker */}
              {current.custom === "mode" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left pt-2">
                  <ModeCard
                    selected={displayMode === "chat"}
                    onClick={() => setDisplayMode("chat")}
                    icon={GraduationCap}
                    title="Простой"
                    emoji="💬"
                    badge="по умолчанию"
                    features={[
                      "Общение как в ChatGPT",
                      "Готовый результат по-человечески",
                      "Картинки, документы, поиск",
                    ]}
                  />
                  <ModeCard
                    selected={displayMode === "developer"}
                    onClick={() => setDisplayMode("developer")}
                    icon={Code2}
                    title="Разработчик"
                    emoji="🖥️"
                    features={[
                      "План и прогресс выполнения",
                      "Видны команды и инструменты",
                      "Diff кода с Apply / Reject",
                    ]}
                  />
                </div>
              )}

              <div className="pt-2 flex flex-wrap items-center justify-center lg:justify-start gap-3">
                {step > 0 && (
                  <button
                    onClick={back}
                    className="btn-ghost text-fg-muted hover:text-fg"
                  >
                    Назад
                  </button>
                )}
                <GradientButton onClick={next} size="lg">
                  {current.action}
                  <ChevronRight className="size-4" />
                </GradientButton>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* ─── RIGHT: animated mockup ──────────────────────────────────── */}
          <div className="block w-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={`mock-${step}`}
                initial={{ opacity: 0, y: 10, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.3 }}
              >
                {step === 0 && <MockChat />}
                {step === 1 && <MockVps />}
                {step === 2 && <MockMode />}
                {step === 3 && <MockReady />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Small footer hint */}
        <p className="mt-10 text-center text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
          ⌘/Ctrl + → пропустить · нажмите по точке чтобы перейти к шагу
        </p>
      </div>
    </div>
  );
}

// ─── ModeCard ─────────────────────────────────────────────────────────────

function ModeCard({
  selected,
  onClick,
  icon: Icon,
  title,
  emoji,
  badge,
  features,
}: {
  selected: boolean;
  onClick: () => void;
  icon: typeof GraduationCap;
  title: string;
  emoji: string;
  badge?: string;
  features: string[];
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ y: -2 }}
      className={cn(
        "relative rounded-card border p-4 text-left transition-all overflow-hidden",
        selected
          ? "border-accent-500/60 bg-accent-500/[0.08] shadow-glow"
          : "border-border/60 hover:border-border-strong bg-bg-card/70 backdrop-blur",
      )}
    >
      {selected && (
        <span className="absolute top-2 right-2 size-5 rounded-full bg-grad-cta grid place-items-center shadow-accent">
          <Check className="size-3 text-white" />
        </span>
      )}
      <div className="flex items-center gap-2 mb-2">
        <span className="text-base">{emoji}</span>
        <Icon className="size-4 text-accent-500" />
        <span className="font-semibold">{title}</span>
        {badge && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-500/20 text-accent-500 font-mono uppercase tracking-wider">
            {badge}
          </span>
        )}
      </div>
      <ul className="text-[12px] text-fg-muted space-y-1">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-1.5">
            <span className="text-accent-500 shrink-0 mt-0.5">·</span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </motion.button>
  );
}

// ─── Mockups ──────────────────────────────────────────────────────────────

function MockShell({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div className="rounded-modal border border-border/60 bg-bg-card/85 backdrop-blur-xl shadow-card overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/50 bg-bg-subtle/60">
        <span className="size-2.5 rounded-full bg-[#ff5f57]" />
        <span className="size-2.5 rounded-full bg-[#ffbd2e]" />
        <span className="size-2.5 rounded-full bg-[#28ca42]" />
        <span className="ml-2 text-[11px] uppercase tracking-wider text-fg-subtle font-mono">
          {label}
        </span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function MockChat() {
  return (
    <MockShell label="aicores · chat">
      <div className="space-y-2.5">
        {/* user */}
        <div className="flex justify-end">
          <div className="max-w-[80%] rounded-2xl bg-grad-cta text-white px-3.5 py-2 text-small shadow-accent">
            Сделай телеграм-бота для погоды
          </div>
        </div>
        {/* ai card */}
        <div className="flex justify-start">
          <div className="max-w-[88%] card p-3 text-small space-y-2">
            <div className="flex items-center gap-2 text-[11px] text-fg-subtle font-mono uppercase tracking-wider">
              <span className="size-1.5 rounded-full bg-success animate-pulse-soft" />
              Шаг 1 из 3 · читаю конфиг
            </div>
            <div className="font-mono text-[12px] bg-bg-subtle/60 rounded px-2 py-1.5 border border-border/40 text-fg-muted">
              ssh root@vps · nginx -t
            </div>
            <motion.div
              className="h-1 rounded-full bg-bg-subtle overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <motion.div
                className="h-full bg-grad-cta"
                initial={{ width: "0%" }}
                animate={{ width: ["0%", "60%", "60%"] }}
                transition={{ duration: 2.4, ease: "easeOut" }}
              />
            </motion.div>
          </div>
        </div>
      </div>
    </MockShell>
  );
}

function MockVps() {
  return (
    <MockShell label="aicores · vps">
      <div className="space-y-2.5">
        {[
          { name: "production-01", host: "157.22.180.8", status: "ok" as const, label: "running" },
          { name: "staging-eu", host: "10.0.4.21", status: "warn" as const, label: "high cpu" },
          { name: "build-runner", host: "build.local", status: "off" as const, label: "off" },
        ].map((s, i) => (
          <motion.div
            key={s.name}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 + i * 0.1 }}
            className="flex items-center gap-3 rounded-lg border border-border/50 bg-bg-subtle/40 px-3 py-2"
          >
            <PulseIndicator status={s.status} />
            <span className="text-small font-medium flex-1 truncate">{s.name}</span>
            <span className="text-[11px] text-fg-subtle font-mono">{s.host}</span>
            <span className="text-[11px] text-fg-muted font-mono">{s.label}</span>
          </motion.div>
        ))}
      </div>
    </MockShell>
  );
}

function MockMode() {
  return (
    <div className="grid grid-cols-2 gap-3">
      <MockShell label="simple">
        <div className="space-y-1.5 text-small">
          <div className="text-fg-muted text-[12px]">Готово за 12 сек</div>
          <div className="card p-2 text-[12px] leading-snug">
            Nginx перезапущен. Все 3 апстрима живы, 200 OK на /health.
          </div>
        </div>
      </MockShell>
      <MockShell label="developer">
        <div className="space-y-1.5 text-[12px]">
          <div className="flex items-center justify-between text-fg-subtle font-mono uppercase tracking-wider text-[10px]">
            <span>план</span>
            <span>2/3</span>
          </div>
          <div className="h-1 rounded-full bg-bg-subtle overflow-hidden">
            <div className="h-full w-2/3 bg-grad-cta" />
          </div>
          <div className="space-y-1 text-fg-muted">
            <div className="flex items-center gap-1.5">
              <Check className="size-3 text-success" /> прочитал /etc/nginx
            </div>
            <div className="flex items-center gap-1.5">
              <Check className="size-3 text-success" /> nginx -t
            </div>
            <div className="flex items-center gap-1.5 text-accent-500">
              <span className="size-1.5 rounded-full bg-accent-500 animate-pulse-soft" />
              systemctl reload
            </div>
          </div>
        </div>
      </MockShell>
    </div>
  );
}

function MockReady() {
  const examples = [
    "Сделай телеграм-бота",
    "Напиши и запусти Python-скрипт",
    "Собери резюме в PDF",
  ];
  return (
    <MockShell label="ready">
      <div className="space-y-2 text-small">
        <div className="text-[11px] uppercase tracking-wider text-fg-subtle font-mono">
          Попробуйте
        </div>
        {examples.map((e, i) => (
          <motion.div
            key={e}
            initial={{ opacity: 0, x: 4 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 + i * 0.12 }}
            className="rounded-lg border border-border/50 bg-bg-subtle/50 px-3 py-2 hover:border-accent-500/40 transition-colors cursor-default"
          >
            «{e}»
          </motion.div>
        ))}
      </div>
    </MockShell>
  );
}
